import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import {
  Box, Typography, Table, TableHead, TableBody, TableCell, TableContainer,
  TableRow, IconButton, Tooltip, Chip, CircularProgress, Card,
  CardContent, Grid, TextField, InputAdornment, Button, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
  Pagination, Stack, Avatar, Badge, Tabs, Tab, Accordion,
  AccordionSummary, AccordionDetails, List, ListItem, ListItemText,
  ListItemIcon, LinearProgress, MenuItem, Select, FormControl, InputLabel
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import InventoryIcon from "@mui/icons-material/Inventory";
import LogoutIcon from "@mui/icons-material/Logout";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import GetAppIcon from "@mui/icons-material/GetApp";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PeopleIcon from "@mui/icons-material/People";
import { useNavigate, Link } from "react-router-dom";

export default function AdminNotas() {
  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("TODOS");
  const [dateFilter, setDateFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, nota: null });
  const [detailDialog, setDetailDialog] = useState({ open: false, nota: null });
  const [cuponsDialog, setCuponsDialog] = useState({ open: false, notaId: null, cupons: [] });
  const [loadingCupons, setLoadingCupons] = useState(false);
  const [motivoReprovacao, setMotivoReprovacao] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const { logout, admin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const loadNotas = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/notas");
      setNotas(res.data);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const res = await api.get("/admin/notas/stats");
      setStats(res.data);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setStatsLoading(false);
    }
  };

  const loadCupons = async (notaId) => {
    try {
      setLoadingCupons(true);
      const res = await api.get(`/admin/cupons?nota_id=${notaId}`);
      setCuponsDialog({ open: true, notaId, cupons: res.data });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Erro ao carregar cupons", severity: "error" });
    } finally {
      setLoadingCupons(false);
    }
  };

  const aprovar = async (id) => {
    try {
      await api.patch(`/admin/aprovar/${id}`);
      setSnackbar({ open: true, message: "Nota aprovada com sucesso!", severity: "success" });
      loadNotas();
      loadStats();
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        return;
      }
      // Verifica se é erro de CORS
      if (error.message && error.message.includes("CORS")) {
        setSnackbar({ 
          open: true, 
          message: "Erro de configuração: O backend precisa permitir o método PATCH no CORS. Contate o administrador.", 
          severity: "error" 
        });
      } else {
        setSnackbar({ open: true, message: error.response?.data?.error || "Erro ao aprovar nota", severity: "error" });
      }
    }
  };

  const reprovar = async (id, motivo) => {
    try {
      await api.patch(`/admin/reprovar/${id}`, { motivo: motivo || "Motivo não informado" });
      setSnackbar({ open: true, message: "Nota reprovada com sucesso!", severity: "success" });
      loadNotas();
      loadStats();
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        return;
      }
      // Verifica se é erro de CORS
      if (error.message && error.message.includes("CORS")) {
        setSnackbar({ 
          open: true, 
          message: "Erro de configuração: O backend precisa permitir o método PATCH no CORS. Contate o administrador.", 
          severity: "error" 
        });
      } else {
        setSnackbar({ open: true, message: error.response?.data?.error || "Erro ao reprovar nota", severity: "error" });
      }
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/admin/export/excel", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `raix_participacoes_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSnackbar({ open: true, message: "Planilha exportada com sucesso!", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Erro ao exportar planilha", severity: "error" });
    }
  };

  const handleConfirmAction = (action, nota) => {
    setMotivoReprovacao("");
    setConfirmDialog({ open: true, action, nota });
  };

  const executeAction = () => {
    if (confirmDialog.action === "aprovar") {
      aprovar(confirmDialog.nota.id);
      setConfirmDialog({ open: false, action: null, nota: null });
      return;
    }
    if (confirmDialog.action === "reprovar") {
      if (!motivoReprovacao || motivoReprovacao.trim().length === 0) {
        setSnackbar({ open: true, message: "Informe o motivo da reprovação.", severity: "warning" });
        return;
      }
      reprovar(confirmDialog.nota.id, motivoReprovacao.trim());
      setConfirmDialog({ open: false, action: null, nota: null });
      return;
    }
  };

  // Funções de filtro e paginação
  const filteredNotas = notas.filter(nota => {
    const matchesSearch = nota.produtor.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         nota.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         nota.cpf_cnpj?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "TODOS" || nota.status === statusFilter;
    
    let matchesDate = true;
    if (dateFilter === "today") {
      const today = new Date().toISOString().split('T')[0];
      matchesDate = nota.criado_em?.split('T')[0] === today;
    } else if (dateFilter === "week") {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      matchesDate = new Date(nota.criado_em) >= weekAgo;
    } else if (dateFilter === "month") {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      matchesDate = new Date(nota.criado_em) >= monthAgo;
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const totalPages = Math.ceil(filteredNotas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedNotas = filteredNotas.slice(startIndex, startIndex + itemsPerPage);

  // Estatísticas locais (fallback)
  const localStats = {
    total: notas.length,
    aprovadas: notas.filter(n => n.status === "APROVADA").length,
    reprovadas: notas.filter(n => n.status === "REPROVADA").length,
    em_analise: notas.filter(n => n.status === "EM_ANALISE").length,
    totalSacas: notas.reduce((sum, n) => sum + (parseInt(n.qtde_sacas) || 0), 0),
    totalCupons: notas.reduce((sum, n) => sum + (parseInt(n.cupons_count) || 0), 0)
  };

  const displayStats = stats || localStats;

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const formatCPF = (cpf) => {
    if (!cpf) return "-";
    const cleaned = cpf.replace(/\D/g, "");
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    return cpf;
  };

  const getFileType = (filename) => {
    if (!filename) return "unknown";
    if (filename.toLowerCase().endsWith(".pdf")) return "pdf";
    if (filename.toLowerCase().match(/\.(png|jpg|jpeg|gif)$/)) return "image";
    return "xml";
  };

  useEffect(() => {
    loadNotas();
    loadStats();
  }, []);

  if (loading && !notas.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center">
          <CircularProgress size={60} />
          <Typography variant="h6" mt={2} color="text.secondary">
            Carregando dados...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "bold", color: "#1b4332", mb: 1 }}>
            📋 Painel Administrativo Raíx
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Gerencie notas fiscais e produtores {admin && `- ${admin.nome}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PeopleIcon />}
            component={Link}
            to="/admin/users"
            sx={{ minWidth: 150 }}
          >
            Usuários
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={handleExport}
            sx={{ minWidth: 150 }}
          >
            Exportar Excel
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ minWidth: 120 }}
          >
            Sair
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab label="Dashboard" />
          <Tab label="Notas Fiscais" />
        </Tabs>
      </Card>

      {/* Dashboard Tab */}
      {tabValue === 0 && (
        <>
          {/* Cards de Estatísticas */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ background: "linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)", color: "white", height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <AssessmentIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {displayStats.total || 0}
                  </Typography>
                  <Typography variant="body2">Total de Notas</Typography>
                  {statsLoading && <LinearProgress sx={{ mt: 1, backgroundColor: "rgba(255,255,255,0.3)" }} color="inherit" />}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ background: "linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)", color: "white", height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <CheckIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {displayStats.aprovadas || 0}
                  </Typography>
                  <Typography variant="body2">Aprovadas</Typography>
                  {displayStats.total > 0 && (
                    <Typography variant="caption">
                      {Math.round((displayStats.aprovadas / displayStats.total) * 100)}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ background: "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)", color: "white", height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <CloseIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {displayStats.reprovadas || 0}
                  </Typography>
                  <Typography variant="body2">Reprovadas</Typography>
                  {displayStats.total > 0 && (
                    <Typography variant="caption">
                      {Math.round((displayStats.reprovadas / displayStats.total) * 100)}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ background: "linear-gradient(135deg, #f57c00 0%, #ff9800 100%)", color: "white", height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Badge badgeContent={displayStats.em_analise || 0} color="error">
                    <InventoryIcon sx={{ fontSize: 40, mb: 1 }} />
                  </Badge>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {displayStats.em_analise || 0}
                  </Typography>
                  <Typography variant="body2">Em Análise</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ background: "linear-gradient(135deg, #1565c0 0%, #2196f3 100%)", color: "white", height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <InventoryIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {displayStats.total_sacas || displayStats.totalSacas || 0}
                  </Typography>
                  <Typography variant="body2">Total de Sacas</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Estatísticas Adicionais */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon /> Total de Produtores
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: "bold", color: "#1b4332" }}>
                    {displayStats.total_produtores || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalOfferIcon /> Total de Cupons Gerados
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: "bold", color: "#1b4332" }}>
                    {displayStats.total_cupons || displayStats.totalCupons || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Notas Tab */}
      {tabValue === 1 && (
        <>
          {/* Filtros e Busca */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Buscar por produtor, email ou CPF/CNPJ..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="TODOS">Todos os Status</MenuItem>
                      <MenuItem value="EM_ANALISE">Em Análise</MenuItem>
                      <MenuItem value="APROVADA">Aprovada</MenuItem>
                      <MenuItem value="REPROVADA">Reprovada</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Período</InputLabel>
                    <Select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      label="Período"
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="today">Hoje</MenuItem>
                      <MenuItem value="week">Última Semana</MenuItem>
                      <MenuItem value="month">Último Mês</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => { loadNotas(); loadStats(); }}
                    fullWidth
                  >
                    Atualizar
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Tabela */}
          <Card>
            <TableContainer>
              <Table>
                <TableHead sx={{ background: "linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)" }}>
                  <TableRow>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Produtor</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Contato</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Sacas</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Cupons</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Data</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedNotas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          Nenhuma nota encontrada
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedNotas.map((n) => (
                      <TableRow key={n.id} hover sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar sx={{ bgcolor: "#1b4332", mr: 2, width: 32, height: 32 }}>
                              <PersonIcon />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                              {n.produtor}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <EmailIcon sx={{ mr: 1, color: "text.secondary", fontSize: 16 }} />
                              <Typography variant="caption">{n.email}</Typography>
                            </Box>
                            {n.telefone && (
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <PhoneIcon sx={{ mr: 1, color: "text.secondary", fontSize: 16 }} />
                                <Typography variant="caption">{n.telefone}</Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${n.qtde_sacas} sacas`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {n.status === "APROVADA" ? (
                            <Tooltip title="Ver cupons">
                              <Chip
                                label={`${n.cupons_count || 0} cupons`}
                                size="small"
                                color="success"
                                variant="outlined"
                                clickable
                                onClick={() => loadCupons(n.id)}
                                icon={<LocalOfferIcon />}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={n.status}
                            color={
                              n.status === "APROVADA"
                                ? "success"
                                : n.status === "REPROVADA"
                                ? "error"
                                : "warning"
                            }
                            sx={{ fontWeight: "bold" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(n.criado_em)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Ver Detalhes">
                              <IconButton
                                color="info"
                                size="small"
                                onClick={() => setDetailDialog({ open: true, nota: n })}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Baixar Arquivo">
                              <IconButton
                                component="a"
                                href={`${api.defaults.baseURL}/uploads/${n.arquivo_xml}`}
                                target="_blank"
                                rel="noreferrer"
                                color="primary"
                                size="small"
                              >
                                {getFileType(n.arquivo_xml) === "pdf" ? (
                                  <PictureAsPdfIcon />
                                ) : getFileType(n.arquivo_xml) === "image" ? (
                                  <ImageIcon />
                                ) : (
                                  <DownloadIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Aprovar Nota">
                              <IconButton
                                color="success"
                                size="small"
                                onClick={() => handleConfirmAction("aprovar", n)}
                                disabled={n.status === "APROVADA"}
                              >
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reprovar Nota">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleConfirmAction("reprovar", n)}
                                disabled={n.status === "REPROVADA"}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Paginação */}
            {totalPages > 1 && (
              <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, page) => setCurrentPage(page)}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </Card>
        </>
      )}

      {/* Dialog de Detalhes */}
      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, nota: null })} maxWidth="md" fullWidth>
        {detailDialog.nota && (
          <>
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "#1b4332" }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{detailDialog.nota.produtor}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {detailDialog.nota.id}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">CPF/CNPJ</Typography>
                  <Typography variant="body1">{formatCPF(detailDialog.nota.cpf_cnpj)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">E-mail</Typography>
                  <Typography variant="body1">{detailDialog.nota.email}</Typography>
                </Grid>
                {detailDialog.nota.telefone && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Telefone</Typography>
                    <Typography variant="body1">{detailDialog.nota.telefone}</Typography>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Quantidade de Sacas</Typography>
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    {detailDialog.nota.qtde_sacas} sacas
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    label={detailDialog.nota.status}
                    color={
                      detailDialog.nota.status === "APROVADA"
                        ? "success"
                        : detailDialog.nota.status === "REPROVADA"
                        ? "error"
                        : "warning"
                    }
                    sx={{ fontWeight: "bold" }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Data de Criação</Typography>
                  <Typography variant="body1">{formatDate(detailDialog.nota.criado_em)}</Typography>
                </Grid>
                {detailDialog.nota.status === "APROVADA" && detailDialog.nota.cupons_count > 0 && (
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      startIcon={<LocalOfferIcon />}
                      onClick={() => loadCupons(detailDialog.nota.id)}
                      fullWidth
                    >
                      Ver {detailDialog.nota.cupons_count} Cupons Gerados
                    </Button>
                  </Grid>
                )}
                {detailDialog.nota.status === "REPROVADA" && detailDialog.nota.motivo_reprovacao && (
                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" color="error">Motivo da Reprovação</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2">{detailDialog.nota.motivo_reprovacao}</Typography>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={getFileType(detailDialog.nota.arquivo_xml) === "pdf" ? <PictureAsPdfIcon /> : <ImageIcon />}
                    component="a"
                    href={`${api.defaults.baseURL}/uploads/${detailDialog.nota.arquivo_xml}`}
                    target="_blank"
                    rel="noreferrer"
                    fullWidth
                  >
                    Visualizar/Download Arquivo
                  </Button>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialog({ open: false, nota: null })}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog de Cupons */}
      <Dialog open={cuponsDialog.open} onClose={() => setCuponsDialog({ open: false, notaId: null, cupons: [] })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocalOfferIcon />
            <Typography variant="h6">Cupons Gerados</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingCupons ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : cuponsDialog.cupons.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              Nenhum cupom encontrado
            </Typography>
          ) : (
            <List>
              {cuponsDialog.cupons.map((cupom, index) => (
                <ListItem key={cupom.id} divider>
                  <ListItemIcon>
                    <LocalOfferIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={cupom.codigo}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          Produtor: {cupom.nome}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          Gerado em: {formatDate(cupom.criado_em)}
                        </Typography>
                      </Box>
                    }
                  />
                  <Chip label={`Nota #${cupom.nota_id}`} size="small" variant="outlined" />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCuponsDialog({ open: false, notaId: null, cupons: [] })}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, nota: null })}>
        <DialogTitle>
          Confirmar Ação
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja {confirmDialog.action === "aprovar" ? "aprovar" : "reprovar"} a nota do produtor{" "}
            <strong>{confirmDialog.nota?.produtor}</strong>?
          </Typography>
          {confirmDialog.action === "reprovar" && (
            <Box mt={2}>
              <TextField
                fullWidth
                label="Motivo da reprovação"
                placeholder="Descreva o motivo da reprovação"
                value={motivoReprovacao}
                onChange={(e) => setMotivoReprovacao(e.target.value)}
                multiline
                minRows={2}
                autoFocus
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, nota: null })}>
            Cancelar
          </Button>
          <Button
            onClick={executeAction}
            color={confirmDialog.action === "aprovar" ? "success" : "error"}
            variant="contained"
          >
            {confirmDialog.action === "aprovar" ? "Aprovar" : "Reprovar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
